import { useMemo } from "react";
import { matchPath, useLocation } from "react-router-dom";

import { useTranslationContext } from "context/TranslationContext";
import { replaceSegmentsFromRoutes } from "helpers/replaceSegmentsFromRoutes";

import { TBreadcrumbItem, TRouteSegment } from "./types";
import { RouteType } from "types/index";
import { routes } from "routes/routes";

const MAX_BREADCRUMB_ITEMS = 6;

const useBreadcrumb = () => {
  const { t } = useTranslationContext();
  const location = useLocation();

  const breadcrumbItems: TBreadcrumbItem[] = useMemo(() => {
    const items: TBreadcrumbItem[] = [];

    const currentPath = getMatchPath(routes, location.pathname);
    const splitedPaths = currentPath?.split("/")?.filter((path) => path) || [];
    const currentSplitedPath =
      location.pathname.split("/")?.filter((path) => path) || "";
    let routeSegment: TRouteSegment = {
      params: [],
      replacer: [],
    };

    // Get all queryparams key & value to be replaced
    for (let i = 0; i < splitedPaths.length; i++) {
      if (splitedPaths[i].startsWith(":")) {
        routeSegment = {
          params: [...routeSegment.params, splitedPaths[i].replaceAll(":", "")],
          replacer: [...routeSegment.replacer, currentSplitedPath[i]],
        };
      }
    }

    // Get all -1 routes from the given exiting route
    const linkedRoutes = splitedPaths
      .map((path, index) => {
        let combinedPath = `/${path}`;

        if (index > 0) {
          let tmpCombinedPath = "";
          for (let i = 0; i <= index; i++) {
            tmpCombinedPath += `/${splitedPaths[i]}`;
          }
          combinedPath = tmpCombinedPath;
        }

        const route = routes.find((route) => route.path === combinedPath);
        return route;
      })
      ?.filter((route) => !!route?.path);

    /**
     * Avoid show breadcrumbs if hideBreadcrumb = true
     * Map all the breadcrumbs that required dynamic value
     */
    const newItems: TBreadcrumbItem[] = linkedRoutes
      .filter((route) => !route?.hideBreadcrumb)
      ?.map((route) => {
        const label = route?.label ? t(route.label) : "";

        const constructedPath = replaceSegmentsFromRoutes({
          route: route?.path || "",
          ...routeSegment,
        });

        return {
          label: label,
          link: constructedPath,
        };
      });

    return [...items, ...newItems];
  }, [location, t]);

  // Split breadcrumbs if too many and join with ...
  const splitedBreadcrumbsItem = useMemo(() => {
    if (breadcrumbItems.length > MAX_BREADCRUMB_ITEMS) {
      const splitedBreadcrumbs = breadcrumbItems.slice(
        Math.max(breadcrumbItems.length - (MAX_BREADCRUMB_ITEMS - 1), 1)
      );
      const shadowedBreadcrumb =
        breadcrumbItems[breadcrumbItems.length - MAX_BREADCRUMB_ITEMS];

      return [
        breadcrumbItems[0],
        {
          ...shadowedBreadcrumb,
          label: "...",
        },
        ...splitedBreadcrumbs,
      ];
    }

    return breadcrumbItems;
  }, [breadcrumbItems]);

  return {
    breadcrumbItems: splitedBreadcrumbsItem,
  };
};

export default useBreadcrumb;

const getMatchPath = (routes: RouteType[], pathname: string): string => {
  let currentPath = "";

  for (const route of routes) {
    if (
      matchPath(
        {
          path: route.path,
          end: true,
        },
        pathname
      )
    ) {
      currentPath = route.path;
      break;
    }
  }

  return currentPath;
};
